import { useState } from "react";
import { DESIGN_OPTIONS, DesignOption } from "@/types/agendaCustomization";
import { cn } from "@/lib/utils";
import { Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface DesignGridProps {
  selectedDesignId?: string;
  customDesignFile?: File;
  onDesignSelect: (designId: string) => void;
  onCustomDesignUpload: (file: File) => void;
}

export const DesignGrid = ({
  selectedDesignId,
  customDesignFile,
  onDesignSelect,
  onCustomDesignUpload,
}: DesignGridProps) => {
  const [showCustomUpload, setShowCustomUpload] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onCustomDesignUpload(file);
      setShowCustomUpload(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-serif text-gray-700 dark:text-gray-300">
        Diseño de Cubierta
      </label>

      {/* Grid de diseños */}
      <div className="grid grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        {DESIGN_OPTIONS.map((design) => (
          <button
            key={design.id}
            onClick={() => onDesignSelect(design.id)}
            title={design.name}
            className={cn(
              "w-12 h-12 rounded border transition-all hover:scale-110 hover:shadow-md",
              selectedDesignId === design.id
                ? "border-primary border-2 shadow-md ring-2 ring-primary/20"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
            )}
            style={{
              background: `linear-gradient(135deg,
                hsl(${(DESIGN_OPTIONS.indexOf(design) * 137.5) % 360}, 70%, 80%),
                hsl(${(DESIGN_OPTIONS.indexOf(design) * 137.5 + 60) % 360}, 70%, 60%))`,
            }}
          >
            <span className="sr-only">{design.name}</span>
          </button>
        ))}
      </div>

      {/* Mostrar nombre del diseño seleccionado */}
      {selectedDesignId && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Diseño seleccionado:{" "}
          <span className="font-medium">
            {DESIGN_OPTIONS.find((d) => d.id === selectedDesignId)?.name}
          </span>
        </p>
      )}

      {/* Opción de subir diseño propio */}
      <div className="mt-4">
        {!showCustomUpload && !customDesignFile ? (
          <button
            onClick={() => setShowCustomUpload(true)}
            className="w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            <Upload className="inline-block mr-2 h-4 w-4" />
            Quiero subir mi propio diseño
          </button>
        ) : customDesignFile ? (
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-primary" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {customDesignFile.name}
              </span>
            </div>
            <button
              onClick={() => {
                onCustomDesignUpload(undefined as any);
                setShowCustomUpload(false);
              }}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Eliminar
            </button>
          </div>
        ) : (
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3">
            <Label htmlFor="custom-design" className="cursor-pointer">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Upload className="h-4 w-4" />
                Seleccionar archivo
              </div>
            </Label>
            <Input
              id="custom-design"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="mt-2"
            />
            <button
              onClick={() => setShowCustomUpload(false)}
              className="text-xs text-gray-500 mt-2 hover:text-gray-700"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
